import React, { useEffect, useState } from 'react';
import { usePreviewSubscription } from 'content/sanity';
import { getClient } from 'content/sanity.server';
import { PortableText } from '@portabletext/react';
import portableTextComponents from 'content/components';
import { NextSeo } from 'next-seo';
import { getSanityImageData } from 'content/SanityImage';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/sl';
import Image from 'next/image';
import { articleSlugsQuery, singleArticleQuery } from 'content/queries';
import { filterDataToSingleItem, generateTableOfContents } from 'content/helpers';
import Frontmatter from 'components/articles/Frontmatter';
import Sidebar from 'components/articles/Sidebar';
import Article from 'models/articles/Article';
import RelatedArticles from 'components/articles/RelatedArticles';
import Author from 'components/articles/Author';
import ShareArticleButton from 'components/articles/ShareArticleButton';
import Footer from 'components/Footer';
import DividerCurve from 'components/curves/DividerCurve';
import Link from 'next/link';
import { ExternalLink } from 'react-feather';
import ArticleShareModal from 'components/articles/ArticleShareModal';
import { Mixpanel } from 'utils/Mixpanel';
import { useRouter } from 'next/router';
dayjs.locale('sl');
dayjs.extend(relativeTime);

export async function getStaticPaths() {
    const pages = await getClient(true).fetch(articleSlugsQuery);

    const paths = pages.map((slug) => ({
        params: {
            slug
        }
    }));

    return {
        paths,
        fallback: true
    };
}

export async function getStaticProps({ params, preview = false }) {
    const queryParams = { slug: params.slug };
    const data = await getClient(preview).fetch(singleArticleQuery, queryParams);

    if (!data || data?.length === 0) return { notFound: true };

    const page = filterDataToSingleItem(data, preview);
    const toc = generateTableOfContents(page.body);

    return {
        props: {
            preview,
            data: {
                page,
                toc,
                query: singleArticleQuery,
                queryParams
            }
        },
        revalidate: 60
    };
}

export default function SingleArticlePage({ data, preview }) {
    const { data: previewData } = usePreviewSubscription(data?.query, {
        params: data?.queryParams ?? {},
        initialData: data?.page,
        enabled: preview
    });
    const [shareDialog, setShareDialog] = useState(false);
    const { asPath } = useRouter();

    useEffect(() => {
        if (shareDialog) {
            Mixpanel.track('Article share dialog opened');
        }
    }, [shareDialog]);

    const article: Article = filterDataToSingleItem(previewData, preview);
    if (!article) return null;

    const mainImage = getSanityImageData({ image: article.mainImage, height: 400, width: 900 });

    const meta = {
        description: article.seo_description || article.excerpt,
        title: article.title,
        openGraph: {
            type: 'article',
            url: `https://kamzavikend.si/${asPath}`,
            article: {
                publishedTime: article.publishedAt,
                modifiedTime: article._updatedAt,
                authors: [`https://kamzavikend.si/namigi/avtor/${article.author.slug.current}`]
            }
        }
    };
    if (mainImage) {
        meta.openGraph['images'] = [{ url: mainImage.url }];
    }

    return (
        <div className="single-article pt-6 md:pt-12">
            <NextSeo title={meta.title} description={meta.description} openGraph={meta.openGraph} />
            <ArticleShareModal article={article} open={shareDialog} onClose={() => setShareDialog(false)} />

            {preview && (
                <Link
                    href={`/api/exit-preview?slug=${article.slug.current}`}
                    className="py-4 px-8 bg-brand shadow-2xl fixed bottom-4 right-4 rounded-full z-50 font-bold inline-flex gap-3"
                >
                    Exit preview mode <ExternalLink />
                </Link>
            )}

            <div className="container">
                <div className="flex items-start gap-6 lg:gap-12">
                    <div className="max-w-4xl">
                        <Frontmatter
                            title={article?.title}
                            publishedAt={article?.publishedAt}
                            updatedAt={article?._updatedAt}
                        />

                        <div className="grid grid-cols-1 lg:grid-cols-2 mt-8 lg:mt-12 gap-4 lg:gap-10">
                            <p className="whitespace-pre-wrap order-3 lg:order-1">{article.excerpt}</p>
                            {mainImage && (
                                <div className="order-1 lg:order-2 relative h-[240px]">
                                    <Image
                                        src={mainImage.url}
                                        className="rounded-3xl"
                                        alt={article?.title}
                                        layout="fill"
                                        objectFit="cover"
                                        priority
                                    />
                                </div>
                            )}

                            <div className="order-2 mb-4 lg:hidden flex justify-between items-center">
                                <Author author={article.author} link />
                                <ShareArticleButton handleClick={() => setShareDialog(true)} />
                            </div>
                        </div>

                        <div className="article-content">
                            {article?.body && <PortableText value={article.body} components={portableTextComponents} />}
                        </div>
                    </div>
                    <div className="min-w-[220px] sticky top-28 hidden lg:block">
                        <Sidebar
                            author={article.author}
                            toc={data?.toc}
                            handleShareClick={() => setShareDialog(true)}
                        />
                    </div>
                </div>
            </div>

            {article?.recentArticles?.length > 0 && (
                <section id="related" className="bg-gray-200 relative mt-12">
                    <DividerCurve styles="absolute w-full left-0 -top-2" />
                    <RelatedArticles articles={article.recentArticles} />
                </section>
            )}
            <section id="footer" className="mt-24">
                <Footer />
            </section>
        </div>
    );
}
